/**
 * The half of D57 that error boundaries cannot do.
 *
 * Boundaries catch render and lifecycle errors. They do not catch a throw
 * inside an event handler, and they do not catch an unhandled promise
 * rejection — which is exactly the fault that cost an afternoon in Stage 0
 * step 06, where an await in a `useEffect` rejected and the screen simply sat
 * there with nothing in the console and nothing on screen.
 *
 * This turns that silence into a visible, dismissible banner. It is the
 * cheapest part of D57 and the highest value.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { createDeduper, describeError, keyOf, type RuntimeProblem } from './failures.js';

/** One report per fault per half-minute. Design rule 12, in miniature. */
const DEDUPE_MS = 30_000;

/** Enough to know something is wrong; not enough to bury the screen. */
const MAX_SHOWN = 3;

export function useRuntimeProblems(): {
  readonly problems: readonly RuntimeProblem[];
  readonly dismiss: (id: string) => void;
} {
  const [problems, setProblems] = useState<readonly RuntimeProblem[]>([]);
  const nextId = useRef(0);

  useEffect(() => {
    const shouldReport = createDeduper(DEDUPE_MS);

    const report = (source: RuntimeProblem['source'], value: unknown): void => {
      const failure = describeError(value);
      if (!shouldReport(keyOf(failure), Date.now())) return;

      nextId.current += 1;
      const problem: RuntimeProblem = { ...failure, id: String(nextId.current), source };

      // Newest last, oldest dropped. A screen full of banners is its own
      // failure, and the operator only needs to know that something is wrong.
      setProblems((current) => [...current, problem].slice(-MAX_SHOWN));
    };

    // `error.error` is absent for cross-origin script errors, where only the
    // message survives. Taking whichever exists keeps those visible too.
    const onError = (event: ErrorEvent): void => {
      report('error', event.error ?? event.message);
    };
    const onRejection = (event: PromiseRejectionEvent): void => {
      report('unhandledrejection', event.reason);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    setProblems((current) => current.filter((p) => p.id !== id));
  }, []);

  return { problems, dismiss };
}

export function RuntimeProblemBanners({
  problems,
  dismiss,
}: {
  readonly problems: readonly RuntimeProblem[];
  readonly dismiss: (id: string) => void;
}): React.JSX.Element | null {
  if (problems.length === 0) return null;

  return (
    <div role="alert">
      {problems.map((problem) => (
        <div key={problem.id}>
          <p>
            Something went wrong in the background. What is on screen may be out of date.{' '}
            <button
              type="button"
              onClick={() => {
                dismiss(problem.id);
              }}
            >
              Dismiss
            </button>
          </p>
          <details>
            <summary>Show details</summary>
            <pre>{problem.detail}</pre>
          </details>
        </div>
      ))}
    </div>
  );
}
